import {
	Alert,
	Box,
	Button,
	ColumnLayout,
	Container,
	Form,
	FormField,
	Header,
	Input,
	SpaceBetween
} from '@cloudscape-design/components'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import FormInput from '../../../components/ControlledFormInputs/Input'
import FormSelect from '../../../components/ControlledFormInputs/Select'
import FormCheckBox from '../../../components/ControlledFormInputs/CheckBox'
import { useForm } from 'react-hook-form'
import { useNewClusterFormStore } from '../../../stores/clusters/newClusterFormStore'
import type { Cluster, ClusterRequest } from '../../../types/cluster'
import { useOktaAuth } from '@okta/okta-react'
import useCreateNewCluster from '../../../hooks/mutate/useCreateNewCluster'
import useAccounts from '../../../hooks/query/useAccounts'
import useClusters from '../../../hooks/query/useClusters'
import useTagsByAccountId from '../../../hooks/query/useTagsByAccountId'
import useSingleTags from '../../../hooks/query/useSingleTags'
import ReadGenericTags from '../../../components/ControlledFormInputs/GenericTagsRead'
import GenericTagsNew from '../../../components/ControlledFormInputs/GenericTagsNew'
import { useGenericTagsManagementFormStore } from '../../../stores/tags/genericTagsManagementFormStore'
import { TagsRequest } from '../../../types/tags'
import useProjects from '../../../hooks/query/useProjects'
import { resolveDistributionListValue } from '../../../helper/projectDistributionListOptions'
import ApproverOverview from '../../../components/ControlledFormInputs/ApproverOverview'
import {
	buildAwsFundingTagRequest,
	fundingDetailsFromParent,
	getAwsFundingApprovals,
	selectClusterFundingParent
} from '../../../helper/awsFundingApproval'
import useCreateNewTags from '../../../hooks/mutate/useCreateNewTags'
import { v4 as uuidv4 } from 'uuid'
import type { Account } from '../../../types/account'
import { buildWorkloadClusterRepoName } from '../../../helper/clusterWorkloadRepo'
const DEPLOYMENT_OPTIONS = [
	{ label: 'DEV:PREP', value: 'DEV:PREP' },
	{ label: 'SB:PREP', value: 'SB:PREP' },
	{ label: 'STAGING:LIVE', value: 'STAGING:LIVE' },
	{ label: 'STAGING:PREP', value: 'STAGING:PREP' },
	{ label: 'PROD:LIVE', value: 'PROD:LIVE' },
	{ label: 'PROD:PREP', value: 'PROD:PREP' }
]
const DATA_CLASSIFICATION_OPTIONS = [
	{ label: 'PUBLIC', value: 'PUBLIC' },
	{ label: 'PRIVATE', value: 'PRIVATE' },
	{ label: 'CONFIDENTIAL-PROPRIETARY', value: 'CONFIDENTIAL-PROPRIETARY' },
	{ label: 'CONFIDENTIAL-PERSONAL', value: 'CONFIDENTIAL-PERSONAL' }
]
const TIER_LEVEL_OPTIONS = [
	{ label: '1', value: '1' },
	{ label: '2a', value: '2a' },
	{ label: '2b', value: '2b' },
	{ label: '3', value: '3' },
	{ label: 'nonprod', value: 'nonprod' }
]

const AWS_EKS_ADDONS: Array<{ key: string; label: string; readOnly?: boolean }> = [
	{ key: 'aws_load_balancer_controller', label: 'AWS Load Balancer Controller', readOnly: true },
	{ key: 'karpenter', label: 'Karpenter', readOnly: true },
	{ key: 'metrics_server', label: 'Metrics Server', readOnly: true },
	{ key: 'external_secrets', label: 'External Secrets', readOnly: true },
	{ key: 'argo_rollouts', label: 'Argo Rollouts' },
	{ key: 'argo_workflows', label: 'Argo Workflows' },
	{ key: 'calico', label: 'Calico' },
	{ key: 'cert_manager', label: 'Cert Manager' },
	{ key: 'cluster_autoscaler', label: 'Cluster Autoscaler' },
	{ key: 'external_dns', label: 'External DNS' },
	{ key: 'kubecost', label: 'Kubecost' },
	{ key: 'kyverno', label: 'Kyverno' },
	{ key: 'falcon_sensor', label: 'Falcon Sensor' },
	{ key: 'keda', label: 'KEDA' }
]

const NONPROD_ENVIRONMENT_OPTIONS = [
	{ label: 'dv', value: 'dv' },
	{ label: 'st', value: 'st' }
]

const isProductionAccountType = (accountType: string | null | undefined): boolean => {
	if (!accountType) return false
	const t = accountType.toLowerCase().replace(/_/g, '-')
	return t === 'production'
}

/** Production accounts onboard pd; non-production accounts choose dv or st. */
const getDefaultEnvironmentForAccountType = (
	accountType: string | null | undefined
): string => {
	if (!accountType) return ''
	return isProductionAccountType(accountType) ? 'pd' : 'dv'
}

const NewCluster: FC = () => {
	const { authState } = useOktaAuth()
	const thisUserEmail = authState?.idToken?.claims.email as string
	const navigate = useNavigate()

	const { data: accounts, isLoading: isLoadingAccounts } = useAccounts()
	const { mutate: getTagsByAccountId, isLoading: isLoadingTags } =
		useTagsByAccountId()
	const [tagsId, setTagsId] = useState<string>('')
	const [tagsLoadStatus, setTagsLoadStatus] = useState<
		'idle' | 'loading' | 'loaded' | 'error'
	>('idle')
	const [tagsViewToggler, setTagsViewToggler] = useState(true)
	const [selectedAccountType, setSelectedAccountType] = useState<
		string | null
	>(null)

	const { mutate: createCluster, isLoading: isSendLoading } =
		useCreateNewCluster()
	const { mutate: createTags, isLoading: isCreateTagsLoading } =
		useCreateNewTags()
	const tagCreationFormStore = useGenericTagsManagementFormStore()
	const { data: existingTags } = useSingleTags(tagsId, { enabled: !!tagsId })
	const { data: clustersList } = useClusters()

	const {
		formValues: clusterFormValues,
		actions: { setFormValues: setClusterFormValues, clearFormValues }
	} = useNewClusterFormStore()

	const {
		control,
		setValue,
		register,
		trigger,
		watch,
		getValues,
		setError,
		formState: { isValid }
	} = useForm({
		mode: 'onChange',
		reValidateMode: 'onChange',
		defaultValues: clusterFormValues
	})

	const watchedForm = watch()

	const distributionListOptions = useMemo(() => {
		if (!accounts || !Array.isArray(accounts)) return []
		const approved = (accounts as Account[]).filter(
			(a) =>
				a.status === 'APPROVED' &&
				a.details?.distributionList &&
				String(a.details?.accountWorkloadType ?? '').trim().toUpperCase() === 'EKS'
		)
		const seen = new Set<string>()
		return approved
			.map((a) => a.details.distributionList as string)
			.filter((dl) => {
				if (seen.has(dl)) return false
				seen.add(dl)
				return true
			})
			.sort()
			.map((dl) => ({ label: dl, value: dl }))
	}, [accounts])

	const distributionListValue =
		typeof clusterFormValues.distributionList === 'object' &&
		clusterFormValues.distributionList !== null &&
		'value' in clusterFormValues.distributionList
			? (clusterFormValues.distributionList as { value: string }).value
			: (clusterFormValues.distributionList as string) ?? ''

	const selectedAccount = useMemo(() => {
		if (!distributionListValue || !accounts?.length) return null
		return (accounts as Account[]).find(
			(a) =>
				a.status === 'APPROVED' &&
				String(a.details?.distributionList ?? '').trim() === distributionListValue
		) ?? null
	}, [distributionListValue, accounts])

	const resolvedAccountType = selectedAccountType ?? selectedAccount?.type ?? null

	const isProductionAccount = useMemo(
		() => isProductionAccountType(resolvedAccountType),
		[resolvedAccountType]
	)

	const selectedEnvironmentRaw =
		getValues('environment') ??
		watchedForm.environment ??
		clusterFormValues.environment
	const selectedEnvironment = (() => {
		if (typeof selectedEnvironmentRaw === 'object' && selectedEnvironmentRaw !== null && 'value' in selectedEnvironmentRaw) {
			return String((selectedEnvironmentRaw as { value: string }).value ?? '').trim()
		}
		return String(selectedEnvironmentRaw ?? '').trim()
	})()

	const applicationStr = (() => {
		const raw = getValues('application') ?? watchedForm.application ?? clusterFormValues.application
		return String(raw ?? '').trim()
	})()

	const { data: projects = [] } = useProjects()
	const accountProjects = useMemo(
		() =>
			projects.filter((project) => {
				if (project.isephemeral) {
					return false
				}
				return (
					resolveDistributionListValue(project.details?.distributionList) ===
					distributionListValue
				)
			}),
		[projects, distributionListValue]
	)
	const fundingParent = useMemo(
		() => selectClusterFundingParent(tagsId, accountProjects, applicationStr),
		[tagsId, accountProjects, applicationStr]
	)
	const { data: projectFundingTags } = useSingleTags(fundingParent?.tagsID || '', {
		enabled: Boolean(fundingParent?.tagsID && fundingParent.tagsID !== tagsId)
	})
	const inheritedFundingTags =
		fundingParent?.kind === 'account' ? existingTags : projectFundingTags

	const displayedClusterName = buildWorkloadClusterRepoName(applicationStr)

	const resolvedEnvironment = isProductionAccount
		? 'pd'
		: selectedEnvironment || 'dv'

	const duplicateClusterBlocking = useMemo(() => {
		if (!displayedClusterName || !resolvedEnvironment || !clustersList?.length)
			return false
		const normalizedEnv = resolvedEnvironment.toLowerCase().trim()
		return (clustersList as Cluster[]).some(
			(c) =>
				(c.details?.clusterName ?? '').trim() === displayedClusterName &&
				(c.details?.environment ?? '').toLowerCase().trim() === normalizedEnv &&
				(c.status === 'APPROVED' || c.status === 'PENDING_APPROVAL')
		)
	}, [displayedClusterName, resolvedEnvironment, clustersList])

	const approvedDvCluster = useMemo(() => {
		if (!displayedClusterName || !clustersList?.length) return null
		return (clustersList as Cluster[]).find(
			(c) =>
				(c.details?.clusterName ?? '').trim() === displayedClusterName &&
				(c.details?.environment ?? '').toLowerCase().trim() === 'dv' &&
				c.status === 'APPROVED'
		) ?? null
	}, [displayedClusterName, clustersList])

	const isStagingEnvironment =
		!isProductionAccount && resolvedEnvironment.toLowerCase().trim() === 'st'

	const stageNetworkApproved =
		selectedAccount?.stgRequest?.status === 'APPROVED'

	const pdRequiresApprovedDv =
		isProductionAccount && !approvedDvCluster

	const stRequiresApprovedDv = isStagingEnvironment && !approvedDvCluster

	const stRequiresStageNetwork = isStagingEnvironment && !stageNetworkApproved

	const accountTagsRequired = tagsViewToggler && !!distributionListValue
	const newTagsRequired = !tagsViewToggler && !!distributionListValue
	const accountTagsReady = tagsLoadStatus === 'loaded' && !!tagsId
	const submitBlockedByTags =
		(accountTagsRequired && (tagsLoadStatus === 'loading' || !accountTagsReady)) ||
		(newTagsRequired && !tagCreationFormStore.isFormValid)

	const submitBlocked =
		duplicateClusterBlocking ||
		pdRequiresApprovedDv ||
		stRequiresApprovedDv ||
		stRequiresStageNetwork ||
		submitBlockedByTags

	const inheritedEksAddons = approvedDvCluster?.details
		?.aws_eks_custom_addons_config as Record<string, boolean> | undefined

	const addonsInheritedFromDv = Boolean(
		isProductionAccount &&
			resolvedEnvironment.toLowerCase().trim() === 'pd' &&
			inheritedEksAddons
	)

	const setClusterFormValuesGuarded = useCallback(
		(fieldName: string, value: unknown) => {
			if (fieldName.startsWith('aws_eks_custom_addons_config.')) {
				const key = fieldName.split('.')[1]
				const addonMeta = AWS_EKS_ADDONS.find((a) => a.key === key)
				if (addonMeta?.readOnly || addonsInheritedFromDv) {
					return
				}
			}
			setClusterFormValues(fieldName, value)
		},
		[addonsInheritedFromDv, setClusterFormValues]
	)

	useEffect(() => {
		register('environment', {
			required: 'Environment is required'
		})
	}, [register])

	useEffect(() => {
		if (!distributionListValue) {
			setTagsId('')
			setTagsLoadStatus('idle')
			setSelectedAccountType(null)
			setClusterFormValues('accountID', '')
			setValue('accountID', '')
			setClusterFormValues('environment', '')
			setValue('environment', null)
			setClusterFormValues('application', '')
			setValue('application', '')
			return
		}
		setTagsLoadStatus('loading')
		setTagsId('')
		getTagsByAccountId(distributionListValue, {
			onSuccess: (data: {
				body?: Array<{
					tagsID?: string
					accountID?: string
					type?: string
					details?: { awsAccountID?: string }
				}>
			}) => {
				const first = data?.body?.[0]
				if (!first?.tagsID) {
					setTagsLoadStatus('error')
					setTagsId('')
					setSelectedAccountType(first?.type ?? null)
					return
				}
				setTagsLoadStatus('loaded')
				setTagsId(first.tagsID)
				const accountType = first?.type ?? null
				setSelectedAccountType(accountType)
				const accountIDFromAccount =
					first?.accountID != null
						? String(first.accountID)
						: first?.details?.awsAccountID != null
							? String(first.details.awsAccountID)
							: ''
				setClusterFormValues('accountID', accountIDFromAccount)
				setValue('accountID', accountIDFromAccount)
				const env = getDefaultEnvironmentForAccountType(accountType)
				if (env) {
					setClusterFormValues('environment', env)
					setValue('environment', env, { shouldValidate: true })
				}
			},
			onError: () => {
				setTagsLoadStatus('error')
				setTagsId('')
				setSelectedAccountType(null)
				setClusterFormValues('accountID', '')
				setValue('accountID', '')
				setClusterFormValues('environment', '')
				setValue('environment', null)
			}
		})
	}, [distributionListValue, setClusterFormValues, setValue, getTagsByAccountId])

	// Application = system alias from tags (loaded when tagsId is set and useSingleTags returns)
	useEffect(() => {
		if (!existingTags?.details) return
		const details = existingTags.details as Record<string, unknown>
		const systemAlias = details.systemAlias ?? details.system_alias
		if (systemAlias == null) return
		const val =
			typeof systemAlias === 'object' &&
			systemAlias !== null &&
			'value' in systemAlias
				? String((systemAlias as { value: string }).value)
				: String(systemAlias)
		if (val) {
			setClusterFormValues('application', val)
			setValue('application', val)
		}
	}, [existingTags?.details, setClusterFormValues, setValue])

	// Production (pd) inherits EKS add-ons from approved dv (defaults + optional selections); all read-only.
	useEffect(() => {
		if (!addonsInheritedFromDv || !inheritedEksAddons) return
		const merged = Object.fromEntries(
			AWS_EKS_ADDONS.map(({ key, readOnly }) => [
				key,
				readOnly ? true : Boolean(inheritedEksAddons[key])
			])
		)
		setClusterFormValues('aws_eks_custom_addons_config', merged)
		setValue('aws_eks_custom_addons_config', merged)
	}, [addonsInheritedFromDv, inheritedEksAddons, setClusterFormValues, setValue])

	const handleSubmit = async () => {
		if (submitBlocked) return
		await trigger()
		if (tagsViewToggler && !isValid) return
		if (tagsViewToggler) {
			if (tagsLoadStatus !== 'loaded' || !tagsId) {
				setError('distributionList', {
					type: 'manual',
					message:
						'Account tags must load successfully before submitting. Select a different distribution list or retry.'
				})
				return
			}
		} else if (distributionListValue && !tagCreationFormStore.isFormValid) {
			return
		}

		const distributionList =
			typeof clusterFormValues.distributionList === 'object'
				? clusterFormValues.distributionList?.value
				: clusterFormValues.distributionList

		const environment = resolvedEnvironment

		const applicationVal = getValues('application') ?? clusterFormValues.application
		const applicationStrSubmit = String(applicationVal ?? '').trim()
		if (!applicationStrSubmit) {
			setError('application', {
				type: 'manual',
				message: 'Application is required.'
			})
			return
		}
		const clusterName = buildWorkloadClusterRepoName(applicationStrSubmit)
		const deployment =
			typeof clusterFormValues.deployment === 'object' &&
			clusterFormValues.deployment !== null &&
			'value' in clusterFormValues.deployment
				? (clusterFormValues.deployment as { value: string }).value
				: clusterFormValues.deployment ?? ''
		const dataClassification =
			typeof clusterFormValues.dataClassification === 'object' &&
			clusterFormValues.dataClassification !== null &&
			'value' in clusterFormValues.dataClassification
				? (clusterFormValues.dataClassification as { value: string }).value
				: clusterFormValues.dataClassification ?? ''
		const tierLevel =
			typeof clusterFormValues.tierLevel === 'object' &&
			clusterFormValues.tierLevel !== null &&
			'value' in clusterFormValues.tierLevel
				? (clusterFormValues.tierLevel as { value: string }).value
				: clusterFormValues.tierLevel ?? ''
		const addonsConfig =
			addonsInheritedFromDv && approvedDvCluster?.details?.aws_eks_custom_addons_config
				? approvedDvCluster.details.aws_eks_custom_addons_config
				: getValues('aws_eks_custom_addons_config') ??
					clusterFormValues.aws_eks_custom_addons_config
		const details: ClusterRequest['details'] = {
			distributionList: distributionList ?? '',
			clusterName,
			application: applicationStrSubmit,
			environment,
			accountID: clusterFormValues.accountID ?? '',
			deployment,
			dataClassification,
			tierLevel,
			...(addonsConfig && { aws_eks_custom_addons_config: addonsConfig })
		}
		const baseCluster: ClusterRequest = {
			status: 'PENDING_APPROVAL',
			requestorID: thisUserEmail,
			action: 'CREATE',
			details
		}

		const newTagsID = uuidv4()
		const fundingChoice = tagsViewToggler ? 'inherit' : 'own'
		const parentTagsID = fundingParent?.tagsID || tagsId
		const tagDetails = tagsViewToggler
			? fundingDetailsFromParent(inheritedFundingTags?.details || {})
			: tagCreationFormStore.formValues
		const tagRequest: TagsRequest = buildAwsFundingTagRequest({
			requestorID: thisUserEmail,
			tagsID: newTagsID,
			parentTagsID,
			fundingChoice,
			details: tagDetails
		})
		createTags(tagRequest, {
			onSuccess: () => {
				createCluster(
					{
						...baseCluster,
						tagsID: newTagsID,
						details: {
							...baseCluster.details,
							fundingChoice,
							parentTagsID
						}
					},
					{
						onSuccess: () => {
							clearFormValues()
							tagCreationFormStore.actions.clearFormValues()
							navigate('/clusters')
						}
					}
				)
			}
		})
	}

	return (
		<Form
			header={<Header variant="h1">New Cluster Request</Header>}
			actions={
				<SpaceBetween direction="horizontal" size="xs">
					<Box>
						<Button
							variant="primary"
							onClick={handleSubmit}
							loading={
								isSendLoading || isLoadingTags || isCreateTagsLoading
							}
							disabled={
								(tagsViewToggler ? !isValid : false) ||
								submitBlocked ||
								isSendLoading ||
								isLoadingTags ||
								isCreateTagsLoading
							}
						>
							Submit
						</Button>
						<Button
							variant="link"
							onClick={() => {
								navigate('/clusters')
								clearFormValues()
							}}
							disabled={isSendLoading}
						>
							Cancel
						</Button>
					</Box>
				</SpaceBetween>
			}
		>
			<SpaceBetween direction="vertical" size="s">
				{duplicateClusterBlocking && (
					<Alert
						type="error"
						header="Duplicate workload repo and environment"
					>
						A cluster request with this workload repo name (
						<strong>{displayedClusterName}</strong>) and environment (
						<strong>{resolvedEnvironment}</strong>) already exists (Approved or
						Pending Approval). Only rejected requests can be resubmitted.
					</Alert>
				)}
				{pdRequiresApprovedDv && (
					<Alert type="error" header="Production cluster onboarding">
						Production cluster onboarding requires an approved development (dv) cluster
						for workload repo <strong>{displayedClusterName}</strong>. Submit and
						approve the dv cluster first.
					</Alert>
				)}
				{stRequiresApprovedDv && (
					<Alert type="error" header="Staging cluster onboarding">
						Staging (st) cluster onboarding requires an approved development (dv) cluster
						for workload repo <strong>{displayedClusterName}</strong>. Submit and
						approve the dv cluster first.
					</Alert>
				)}
				{stRequiresStageNetwork && (
					<Alert type="error" header="Stage Network Details required">
						Staging (st) cluster onboarding requires Stage Network Details to be
						approved on the account. Complete and approve Stage Network Details on the
						account before submitting an st cluster request.
					</Alert>
				)}
				{addonsInheritedFromDv && approvedDvCluster && (
					<Alert type="info" header="Add-ons inherited from Non-Production">
						All EKS add-ons (including optional ones selected on the non-production
						cluster) are inherited and cannot be changed for production onboarding.
					</Alert>
				)}
				{!isLoadingAccounts && distributionListOptions.length === 0 && (
					<Alert type="warning" header="No approved EKS accounts">
						Distribution list dropdown is empty. Cluster onboarding requires an
						approved account with workload type <strong>EKS</strong>. Ensure{' '}
						<code>REACT_APP_API_URL</code> points to the backend and that at least
						one such account exists.
					</Alert>
				)}
				<Container>
					<SpaceBetween direction="vertical" size="l">
						<FormSelect
							control={control}
							setFormValues={setClusterFormValues}
							name="distributionList"
							label="Distribution List"
							placeholder="Select distribution list"
							options={distributionListOptions}
							rules={{ required: 'Please select a distribution list' }}
						/>

						<FormInput
							control={control}
							setFormValues={setClusterFormValues}
							name="accountID"
							label="Account ID"
							placeholder="AWS Account ID Added"
							type="text"
							disabled={true}
							rules={{ required: 'Account ID is required' }}
						/>

						{isProductionAccount ? (
							<FormField label="Environment">
								<Input value={resolvedEnvironment} readOnly disabled />
							</FormField>
						) : (
							<FormSelect
								control={control}
								setFormValues={setClusterFormValues}
								name="environment"
								label="Environment"
								placeholder="Select environment"
								options={NONPROD_ENVIRONMENT_OPTIONS}
								rules={{ required: 'Please select an environment (dv or st)' }}
								disabled={!distributionListValue}
							/>
						)}

						<FormField label="" description="">
							<ColumnLayout columns={3} variant="default">
								<FormSelect
									control={control}
									setFormValues={setClusterFormValues}
									name="deployment"
									label="Deployment"
									placeholder="Select Deployment"
									options={DEPLOYMENT_OPTIONS}
									rules={{ required: 'Please select Deployment' }}
								/>
								<FormSelect
									control={control}
									setFormValues={setClusterFormValues}
									name="dataClassification"
									label="Data Classification"
									placeholder="Select Data Classification"
									options={DATA_CLASSIFICATION_OPTIONS}
									rules={{ required: 'Please select Data Classification' }}
								/>
								<FormSelect
									control={control}
									setFormValues={setClusterFormValues}
									name="tierLevel"
									label="Tier Level"
									placeholder="Select Tier Level"
									options={TIER_LEVEL_OPTIONS}
									rules={{ required: 'Please select Tier Level' }}
								/>
							</ColumnLayout>
						</FormField>

						<FormField
							label="Application"
						>
							<FormInput
								control={control}
								setFormValues={setClusterFormValues}
								name="application"
								label=""
								placeholder="Application name"
								disabled={true}
								rules={{
									required: 'Application is required',
									validate: (v: string) =>
										!v ||
										/^[a-z]{1,5}$/.test(v) ||
										'Only lowercase letters, 1–5 characters'
								}}
							/>
						</FormField>

						<FormField
							label="Workload Cluster Repo"
						>
							<Input
								value={displayedClusterName}
								readOnly
								disabled
								placeholder="Enter application to see repo name"
							/>
						</FormField>

						<FormField
							label="AWS EKS add-ons"
						>
							<ColumnLayout columns={3} variant="text-grid">
								{AWS_EKS_ADDONS.map(({ key, label, readOnly }) => (
									<FormCheckBox
										key={key}
										control={control}
										setFormValues={setClusterFormValuesGuarded}
										name={`aws_eks_custom_addons_config.${key}`}
										label=""
										check_name={label}
										disabled={!!readOnly || addonsInheritedFromDv}
									/>
								))}
							</ColumnLayout>
						</FormField>
					</SpaceBetween>
				</Container>

				{distributionListValue && (
					<Container
						header={
							<Header
								variant="h2"
								description=""
								actions={
									tagsId || !tagsViewToggler ? (
										<SpaceBetween direction="horizontal" size="xs">
											{tagsViewToggler ? (
												<Button
													variant="link"
													onClick={() => setTagsViewToggler(false)}
												>
													+ Create New Tags
												</Button>
											) : (
												<Button
													variant="link"
													onClick={() => setTagsViewToggler(true)}
												>
													- Switch to Original Tags
												</Button>
											)}
										</SpaceBetween>
									) : tagsViewToggler && tagsLoadStatus === 'error' ? (
										<Button
											variant="link"
											onClick={() => setTagsViewToggler(false)}
										>
											+ Create New Tags
										</Button>
									) : undefined
								}
							>
								Tags Details
							</Header>
						}
					>
						<SpaceBetween direction="vertical" size="m">
							{tagsViewToggler && tagsLoadStatus === 'error' && (
								<Alert type="error" header="Account tags could not be loaded">
									Tags for the selected account failed to load. Submit is disabled
									until tags load successfully. Try selecting the distribution list
									again, or use Create New Tags once tags are available.
								</Alert>
							)}
							{tagsViewToggler && tagsLoadStatus === 'loading' && (
								<Alert type="info" header="Loading account tags">
									Loading tags for the selected account. Submit will be enabled once
									tags load successfully.
								</Alert>
							)}
							{!tagsViewToggler && !tagCreationFormStore.isFormValid && (
								<Alert type="info" header="Complete tag details">
									Fill in all required tag fields before submitting.
								</Alert>
							)}
							{tagsId || !tagsViewToggler ? (
								tagsViewToggler ? (
									<>
										<ReadGenericTags
											tagsId={fundingParent?.tagsID || tagsId}
											showApprovalStatus={false}
										/>
										<ApproverOverview
											approvalStatus={getAwsFundingApprovals()}
											isDisabled
										/>
									</>
								) : (
									<>
										<GenericTagsNew />
										<ApproverOverview
											approvalStatus={getAwsFundingApprovals()}
											isDisabled
										/>
									</>
								)
							) : null}
						</SpaceBetween>
					</Container>
				)}
			</SpaceBetween>
		</Form>
	)
}

export default NewCluster
